import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Headphones, Video, Lock, ArrowRight, Search } from "lucide-react";

export default function ExplainersPublic() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [fmt, setFmt] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (search) params.q = search;
    if (fmt) params.fmt = fmt;
    api.get("/paper-explainers", { params })
      .then((r) => { if (!cancelled) setItems(r.data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, fmt]);

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="explainers-public-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PREMIUM SECTION</div>
        <h1 className="font-serif text-4xl lg:text-6xl text-kb-ink mt-2 leading-tight">
          Papers, <em className="italic text-kb-primary">explained</em>.
        </h1>
        <p className="mt-5 text-kb-ink-muted max-w-2xl">
          Talking-head videos and audiobook-style explainers of seminal and trending research papers — with
          notation walk-throughs, intuition, and runnable code where possible.
        </p>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-kb-ink-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by paper title, topic, or tag…"
              className="kb-input text-sm pl-9 w-full"
              data-testid="explainers-search-input"
            />
          </div>
          {[{ v: "", l: "All" }, { v: "video", l: "Videos" }, { v: "audio", l: "Audiobooks" }].map((opt) => (
            <button key={opt.v} onClick={() => setFmt(opt.v)}
              className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border ${fmt === opt.v ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted"}`}
              data-testid={`explainers-fmt-${opt.v || "all"}`}>
              {opt.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
            No explainers found.
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="explainers-grid">
            {items.map((p) => {
              const Icon = p.format === "audio" ? Headphones : Video;
              return (
                <Link to={`/explainers/${p.id}`} key={p.id} className="kb-card overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`explainer-card-${p.id}`}>
                  {p.cover_image_url && (
                    <div className="h-32 bg-slate-100 overflow-hidden">
                      <img src={p.cover_image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {p.format}</span>
                      <span className="kb-mono text-[10px] text-kb-ink-muted">{p.duration_min} min</span>
                    </div>
                    <h3 className="font-serif text-lg text-kb-ink mt-3 leading-snug">{p.paper_title}</h3>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                      {(p.paper_authors || []).slice(0, 3).join(", ")}
                      {(p.paper_authors?.length || 0) > 3 ? " et al." : ""}
                      {p.paper_year ? ` · ${p.paper_year}` : ""}
                    </div>
                    {p.summary && <p className="text-sm text-kb-ink-muted mt-3 flex-1 line-clamp-3">{p.summary}</p>}
                    <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
                      <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                        {p.locked ? <span className="inline-flex items-center gap-1 text-kb-primary"><Lock className="w-3 h-3" /> PREMIUM</span> : "FREE PREVIEW"}
                      </span>
                      <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
                        Open <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-14 kb-card p-8 text-center">
          <h3 className="font-serif text-2xl text-kb-ink">Want full library access?</h3>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">Premium subscribers get all video transcripts, audiobooks, and code repositories.</p>
          <Link to="/pricing" className="kb-btn-primary mt-5 inline-flex" data-testid="explainers-pricing-cta">
            View plans <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
